// @flow

export type ConfigApp = {
    logo: string,
    name: string,
    color: string,
    colorLighter: string
};

export type ConfigAppInput = {
    logo?: string,
    name?: string
};

export type ConfigRoutes = {
    forgotPassword: string,
    signin: string,
    signup: string
};

export type ConfigRoutesInput = {
    forgotPassword?: string,
    signin?: string,
    signup?: string
};

export type ConfigFields = {
    usernameLabel: string,
    usernamePlaceholder: string,
    passwordLabel: string,
    passwordPlaceholder: string,
    chooseUsernameLabel: string,
    chooseUsernamePlaceholder: string,
    choosePasswordLabel: string,
    choosePasswordPlaceholder: string,
    choosePasswordConfirmLabel: string,
    choosePasswordConfirmPlaceholder: string,
    verificationCodeLabel: string,
    verificationCodePlaceholder: string,
    emailLabel: string,
    emailPlaceholder: string
};

export type ConfigFieldsInput = {
    usernameLabel?: string,
    usernamePlaceholder?: string,
    passwordLabel?: string,
    passwordPlaceholder?: string,
    chooseUsernameLabel?: string,
    chooseUsernamePlaceholder?: string,
    choosePasswordLabel?: string,
    choosePasswordPlaceholder?: string,
    choosePasswordConfirmLabel?: string,
    choosePasswordConfirmPlaceholder?: string,
    verificationCodeLabel?: string,
    verificationCodePlaceholder?: string,
    emailLabel?: string,
    emailPlaceholder?: string
};


export type ConfigForgotPassword = {
    title: string,
    description: string,
    submitButton: string,
    backLink: string,
    loadingText: string
};

export type ConfigForgotPasswordInput = {
    title?: string,
    description?: string,
    submitButton?: string,
    backLink?: string,
    loadingText?: string
};

export type ConfigSignin = {
    submitButton: string,
    loadingText: string,
    signupLink: string,
    confirmLink: string,
    forgotPasswordLink: string
};

export type ConfigSigninInput = {
    submitButton?: string,
    loadingText?: string,
    signupLink?: string,
    confirmLink?: string,
    forgotPasswordLink?: string
};

export type ConfigSignup = {
    title: string,
    description: string,
    submitButton: string,
    backLink: string,
    loadingText: string
};

export type ConfigSignupInput = {
    title?: string,
    description?: string,
    submitButton?: string,
    backLink?: string,
    loadingText?: string
};

export type ConfigChoosePassword = {
    title: string,
    description: string,
    submitButton: string,
    backLink: string,
    loadingText: string
};

export type ConfigChoosePasswordInput = {
    title?: string,
    description?: string,
    submitButton?: string,
    backLink?: string,
    loadingText?: string
};

export type ConfigSignupConfirm = {
    title: string,
    description: string,
    submitButton: string,
    resendLink: string,
    backLink: string,
    loadingText: string
};

export type ConfigSignupConfirmInput = {
    title?: string,
    description?: string,
    submitButton?: string,
    resendLink?: string,
    backLink?: string,
    loadingText?: string
};

export type ConfigConfirm = {
    title: string,
    description: string,
    submitButton: string,
    backLink: string,
    loadingText: string
};

export type ConfigConfirmInput = {
    title?: string,
    description?: string,
    submitButton?: string,
    backLink?: string,
    loadingText?: string
};

export type ConfigSignupConfirmResend = {
    loadingText: string
};

export type ConfigSignupConfirmResendInput = {
    loadingText?: string
};


export type Config = {
    app: ConfigApp,
    routes: ConfigRoutes,
    fields: ConfigFields,
    forgotPassword: ConfigForgotPassword,
    signin: ConfigSignin,
    signup: ConfigSignup,
    choosePassword: ConfigChoosePassword,
    confirm: ConfigConfirm,
    signupConfirm: ConfigSignupConfirm,
    signupConfirmResend: ConfigSignupConfirmResend
};

export type ConfigInput = {
    app?: ConfigAppInput,
    routes?: ConfigRoutesInput,
    fields?: ConfigFields,
    forgotPassword?: ConfigForgotPasswordInput,
    signin?: ConfigSigninInput,
    signup?: ConfigSignupInput,
    choosePassword?: ConfigChoosePasswordInput,
    confirm?: ConfigConfirm,
    signupConfirm?: ConfigSignupConfirm,
    signupConfirmResend?: ConfigSignupConfirmResendInput
};

export const DefaultConfig: Config = {
    app: {
        logo: ``,
        name: ``,
        color: `#1772c1`,
        colorLighter: `#4A90E2`
    },
    routes: {
        confirm: `/confirm`,
        forgotPassword: `/forgot-password`,
        signin: `/`,
        signup: `/sign-up`,
        auth0Landing: '/auth0Landing'
    },
    fields: {
        usernameLabel: `Username`,
        usernamePlaceholder: `Username`,
        passwordLabel: `Password`,
        passwordPlaceholder: `Password`,
        chooseUsernameLabel: `Username you were invited with`,
        chooseUsernamePlaceholder: `Username`,
        choosePasswordLabel: `Choose a new password`,
        choosePasswordPlaceholder: `Password`,
        choosePasswordConfirmLabel: `Confirm your new password`,
        choosePasswordConfirmPlaceholder: `Password`,
        verificationCodeLabel: `Verification Code`,
        verificationCodePlaceholder: `e.g. 123456`,
        emailLabel: `Email you were invited with`,
        emailPlaceholder: ``
    },
    signin: {
        submitButton: `Sign In`,
        loadingText: `Signing in...`,
        signupLink: `Sign up`,
        confirmLink: `Got an account verification code?`,
        forgotPasswordLink: `Forgot your password?`
    },
    signup: {
        title: `Sign up`,
        description: ``,
        submitButton: `Sign Up`,
        backLink: `Back to sign-in`,
        loadingText: `Signing up...`
    },
    choosePassword: {
        title: `Choose your password`,
        description: `We just sent a verification code to your email address. Please enter it below and choose a new password. Your password must be minimum 8 characters, including uppercase, lowercase, symbol and number characters.`,
        submitButton: `Change Password`,
        backLink: `Back to sign-in`,
        loadingText: `Changing password...`
    },
    forgotPassword: {
        title: `Reset your password`,
        description: `If you've forgotten your password, type your username in the field below. We'll send you an email to make sure it's you.`,
        submitButton: `Reset Password`,
        backLink: `Back to sign-in`,
        loadingText: `Requesting password reset...`
    },
    confirm: {
        title: `Confirm your account`,
        description: `If you have a verification code, please enter it below.`,
        submitButton: `Confirm`,
        backLink: `Back to sign-in`,
        loadingText: `Confirming your account...`
    },
    signupConfirm: {
        title: `Confirm your account`,
        description: `We just sent a verification code to your email address<verificationValue>. Please enter it below.`,
        submitButton: `Confirm`,
        resendLink: `Resend verification code`,
        backLink: `Back to sign-in`,
        loadingText: `Confirming your account...`
    },
    signupConfirmResend: {
        loadingText: `Sending verification code...`
    }
};
